// src/utils/siteReachable.js
// Does our own site answer right now? (09/16/2026, fifth review round of the phone speed work.)
//
// Why: the browser's "online" flag only says a network is attached, not that anything gets through. On one bar at a
// showing, or on a flag that came back without anything behind it, it says online while every request hangs. A
// reload there ends on the phone's own "not connected" page, which has no buttons: the dead end the whole round
// exists to remove. So before the app reloads itself (utils/lazyPage.js, App.js), and before a wait for the signal
// ends, it asks for one tiny file of its own and trusts only an answer.
//
// The file is the site's manifest.json, which the site already serves at its own address. It is fetched fresh
// (no stored copy, and a new query value each time, so nothing on the way can answer for the site) and the check
// gives up after about 4 seconds. Any answer from the server counts, even an error page: the site was reached.
// A network error, a timeout, or a browser with no fetch at all counts as no answer. It never throws.
//
// This is not a database read: it reads no account and touches no Firestore, so it is allowed before sign-in.
// It is part of the download every launch needs, so it imports nothing at all.

export const REACH_CHECK_TIMEOUT_MS = 4000;

let checksMade = 0;

// The address of our own manifest, following the folder the app is served from (empty at the site's root).
function manifestAddress() {
  const base = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  checksMade += 1;
  return `${base}/manifest.json?reach=${Date.now().toString(36)}-${checksMade}`;
}

function browserFetch() {
  try {
    return typeof window !== "undefined" && typeof window.fetch === "function" ? window.fetch.bind(window) : null;
  } catch {
    return null;
  }
}

// Resolves true when the site answered within the time allowed, false otherwise. `fetchImpl` stands in for the
// browser's fetch in tests (null means "this browser has none").
export function canReachSite({ fetchImpl = browserFetch(), timeoutMs = REACH_CHECK_TIMEOUT_MS } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const finish = (answer) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(answer);
    };
    try {
      if (typeof fetchImpl !== "function") {
        finish(false);
        return;
      }
      const controller = typeof AbortController === "function" ? new AbortController() : null;
      timer = setTimeout(() => {
        finish(false);
        try {
          controller?.abort();
        } catch {
          // Calling the request off is a courtesy; the answer is already no.
        }
      }, timeoutMs);
      const options = { cache: "no-store", credentials: "same-origin" };
      if (controller) options.signal = controller.signal;
      Promise.resolve(fetchImpl(manifestAddress(), options)).then(
        () => finish(true),
        () => finish(false),
      );
    } catch {
      finish(false);
    }
  });
}
